import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronRight, Plus, Search, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT } from "@/lib/i18n";
import { inr } from "@/lib/format";
import { Card, EmptyState, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Chips } from "@/components/form";
import { StatusPill } from "@/components/StatusPill";
import { AddTenantSheet } from "@/components/AddTenantSheet";

export default function Tenants() {
  const { t } = useT();
  const { property, reloadProperties } = useAuth();
  const [status, setStatus] = useState<"active" | "moved_out">("active");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const { data, loading, error, reload } = useApi<any[]>(property ? `/tenants?property_id=${property.id}&status=${status}` : null);
  const list = (data || []).filter((x) => !q || x.name.toLowerCase().includes(q.toLowerCase()) || (x.phone || "").includes(q) || String(x.room_name).includes(q));
  const addBtn = <Button data-testid="tenants-add-btn" onClick={() => setOpen(true)} className="h-11 rounded-full bg-emerald-700 hover:bg-emerald-800"><Plus className="h-4 w-4 mr-1" />{t("add")}</Button>;
  return (
    <div>
      <PageTitle title={t("tenants")} sub={data ? `${data.length}` : undefined} right={addBtn} />
      <div className="space-y-3 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input data-testid="tenants-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")}
            className="w-full h-12 rounded-xl border border-slate-200 bg-white pl-10 pr-3 outline-none focus:ring-2 focus:ring-emerald-600" />
        </div>
        <Chips testId="tenants-status-filter" value={status} onChange={setStatus} options={[{ value: "active", label: t("active") }, { value: "moved_out", label: t("moved_out") }]} />
      </div>
      {loading && !data ? <ListSkeleton /> : error ? <ErrorState message={error} onRetry={reload} /> : list.length === 0 ? (
        <EmptyState testId="tenants-empty" icon={Users} title={t("no_tenants")} text={t("no_tenants_text")} action={status === "active" ? addBtn : undefined} />
      ) : (
        <div className="space-y-2.5 pg-stagger">
          {list.map((x) => (
            <Link key={x.id} to={`/tenants/${x.id}`} data-testid={`tenant-card-${x.id}`}>
              <Card className="p-4 flex items-center gap-3 pg-tap hover:border-emerald-300 mb-2.5">
                <div className="h-11 w-11 rounded-full bg-emerald-100 text-emerald-800 font-bold grid place-items-center shrink-0">{x.name.slice(0, 1)}</div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold truncate">{x.name}</p>
                  <p className="text-xs text-slate-500">{t("room")} {x.room_name} · {t("bed")} {x.bed_label} · {inr(x.monthly_rent)}</p>
                </div>
                {x.current_status && <StatusPill status={x.current_status} testId={`tenant-status-${x.id}`} />}
                <ChevronRight className="h-4 w-4 text-slate-400" />
              </Card>
            </Link>
          ))}
        </div>
      )}
      <AddTenantSheet open={open} onClose={() => setOpen(false)} onSaved={() => { reload(); reloadProperties(); }} />
    </div>
  );
}
